// ==========================================
// Applied Filters UI Handler
// ==========================================
class AppliedFiltersUI {
  constructor({
    wrapperSelector = '.pa-clear-filter-wrapper',
    onRemove = null,
    onClearAll = null
  } = {}) {
    this.wrapperSelector = wrapperSelector;
    this.wrapper = document.querySelector(this.wrapperSelector);
    this.onRemove = onRemove;
    this.onClearAll = onClearAll;

    // Mapping for common Searchspring internal field names to readable labels
    this.facetLabels = {
      'variant_size': 'Size',
      'vendor': 'Brand',
      'ss_price': 'Price',
      'color': 'Color',
      'color_family': 'Color',
      'product_type': 'Type'
    };

    if (!this.wrapper) return;
    this.init();
  }

  init() {
    this.render();
    // Re-render when URL hash changes (Searchspring native actions or browser back/forward)
    window.addEventListener('hashchange', () => this.render());
    window.addEventListener('popstate', () => this.render());
  }

  /**
   * Parses the URL hash to extract all active Searchspring filters.
   * Format: #/filter:field1:value1/filter:field2:value2
   * Excludes sorting parameters (sort:, sort_by, etc.) but keeps price filters
   */
  getAppliedFilters() {
    const hash = window.location.hash;
    const filters = [];
    if (!hash || !hash.includes('/filter:')) return [];

    // Split by the Searchspring filter prefix
    const segments = hash.split('/filter:');
    segments.forEach((segment, index) => {
      if (index === 0) return; // Skip the base part of the hash

      let cleanSegment = segment.replace(/\/$/, ''); // Remove trailing slashes

      // Remove any /sort:... portions that may be appended to this segment
      // This handles URLs like: /filter:variant_size:XS/sort:ss_price:asc/filter:...
      cleanSegment = cleanSegment.replace(/\/sort:[^\/]*/gi, '');

      const colonIndex = cleanSegment.indexOf(':');

      if (colonIndex !== -1) {
        const field = cleanSegment.substring(0, colonIndex);
        const value = cleanSegment.substring(colonIndex + 1);

        // Skip empty values after cleaning
        if (!value || value.trim() === '') {
          return;
        }

        filters.push({
          field: field,
          value: value,
          id: `/filter:${field}:${value}` // Fragment uniquely identifying this filter
        });
      }
    });

    return filters;
  }

  render() {
    if (!this.wrapper) return;
    this.wrapper.innerHTML = '';

    const applied = this.getAppliedFilters();
    if (applied.length === 0) {
      this.wrapper.style.display = 'none';
      return;
    }

    this.wrapper.style.display = 'flex';
    const fragment = document.createDocumentFragment();

    applied.forEach(filter => {
      const tag = document.createElement('span');
      tag.className = 'pa-applied-filter-tag';
      tag.setAttribute('role', 'listitem');

      const label = this.formatField(filter.field);
      const value = this.formatValue(filter.value);

      // Remove button (×)
      const removeBtn = document.createElement('button');
      removeBtn.className = 'pa-applied-filter-remove';
      removeBtn.setAttribute('aria-label', `Remove filter ${label}: ${value}`);
      removeBtn.innerHTML = '×';
      removeBtn.type = 'button';
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof this.onRemove === 'function') {
          this.onRemove(filter);
        }
      });

      // Filter text (Label: Value)
      const content = document.createElement('span');
      content.className = 'pa-applied-filter-content';
      content.innerHTML = `<span class="pa-applied-filter-value">${value}</span>`;

      tag.appendChild(removeBtn);
      tag.appendChild(content);
      fragment.appendChild(tag);
    });

    // Clear All button
    const clearAllBtn = document.createElement('button');
    clearAllBtn.className = 'pa-applied-filter-clearall';
    clearAllBtn.type = 'button';
    clearAllBtn.innerHTML = '<u>Clear all</u>';
    clearAllBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof this.onClearAll === 'function') {
        this.onClearAll();
      }
    });
    fragment.appendChild(clearAllBtn);

    this.wrapper.appendChild(fragment);
  }

  formatField(field) {
    if (this.facetLabels[field]) return this.facetLabels[field];
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  formatValue(value) {
    if (!value) return '';
    // Handle Searchspring-style space encoding and standard URI encoding
    let decoded = value.replace(/\$2520/gi, ' ');
    try {
      decoded = decodeURIComponent(decoded);
    } catch (e) { }
    return decoded.replace(/[-_]/g, ' ');
  }
}

// --- Integration with FilterDrawerHandler ---
// This ensures the AppliedFiltersUI and FilterDrawerHandler talk to each other correctly.
document.addEventListener('DOMContentLoaded', () => {
  // Ensure we can reference the FilterDrawerHandler instance
  if (!window.FilterDrawerHandlerInstance) {
    const origInit = FilterDrawerHandler.prototype.init;
    FilterDrawerHandler.prototype.init = function () {
      window.FilterDrawerHandlerInstance = this;
      origInit.call(this);
    };
  }

  // Integration Logic
  setTimeout(() => {
    /**
     * Removes a specific filter from the current URL hash and notifies systems.
     */
    function handleRemove(filter) {
      const url = new URL(window.location.href);
      let hash = url.hash;

      // Remove the specific filter segment (id) from the hash
      const newHash = hash.replace(filter.id, '');

      // Clean up the hash for visual consistency
      const cleanedHash = newHash.replace(/\/+/g, '/').replace(/\/$/, '').replace(/^#\/$/, '');
      url.hash = (cleanedHash === '#' || cleanedHash === '') ? '' : cleanedHash;

      // Strip pagination when filters change
      url.searchParams.delete('page');

      // Update URL without page reload
      window.history.pushState(null, '', url.toString());

      // Manually trigger events so Searchspring and other components react
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      window.dispatchEvent(new PopStateEvent('popstate'));

      // Sync state with the drawer handler if it's active
      if (window.FilterDrawerHandlerInstance) {
        window.FilterDrawerHandlerInstance.captureAppliedFilters && window.FilterDrawerHandlerInstance.captureAppliedFilters();
        window.FilterDrawerHandlerInstance.updateApplyButtonState && window.FilterDrawerHandlerInstance.updateApplyButtonState();
      }
    }

    /**
     * Clears all filters and resets pagination.
     */
    function handleClearAll() {
      if (window.FilterDrawerHandlerInstance && window.FilterDrawerHandlerInstance.clearAllFilters) {
        window.FilterDrawerHandlerInstance.clearAllFilters();
      } else {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('page');
        window.history.pushState(null, '', url.toString());
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }

    // Initialize the UI instance
    window.appliedFiltersUI = new AppliedFiltersUI({
      wrapperSelector: '.pa-clear-filter-wrapper',
      onRemove: handleRemove,
      onClearAll: handleClearAll
    });

  }, 500); // Small delay to allow Searchspring contents to begin loading


  // Sticky on scroll for .pa-sticky-mobile (class only)
  function initStickyMobile() {
    const stickyEl = document.querySelector('.pa-sticky-mobile.filter-stick');
    if (!stickyEl) return;

    let stickyTop = null;
    let isActive = false;

    function updateStickyTop() {
      stickyEl.classList.remove('pa-sticky-active');
      stickyTop = stickyEl.getBoundingClientRect().top + window.scrollY;
    }

    function onScroll() {
      if (window.innerWidth >= 768) {
        // Remove class if not mobile
        if (isActive) {
          stickyEl.classList.remove('pa-sticky-active');
          isActive = false;
        }
        return;
      }
      if (stickyTop === null) updateStickyTop();
      if (window.scrollY >= stickyTop - 56) {
        if (!isActive) {
          stickyEl.classList.add('pa-sticky-active');
          isActive = true;
        }
      } else {
        if (isActive) {
          stickyEl.classList.remove('pa-sticky-active');
          isActive = false;
        }
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      updateStickyTop();
      onScroll();
    });
    window.addEventListener('load', () => {
      updateStickyTop();
      onScroll();
    });
    updateStickyTop();
    onScroll();
  }

  function waitForStickyMobile(cb) {
    if (document.querySelector('.pa-sticky-mobile')) {
      cb();
    } else {
      const observer = new MutationObserver(() => {
        if (document.querySelector('.pa-sticky-mobile')) {
          observer.disconnect();
          cb();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }
  waitForStickyMobile(initStickyMobile);

});

class SearchCountWatcher {
  constructor(selector, callback) {
    this.selector = selector;
    this.callback = callback;
    this.target = document.querySelector(this.selector);

    if (!this.target) return;

    // Run once on page load
    this.run();

    // Start observing changes
    this.observe();
  }

  run() {
    const value = this.target.textContent.trim();
    this.callback(value, this.target);
  }

  observe() {
    this.observer = new MutationObserver(() => {
      this.run();
    });

    this.observer.observe(this.target, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

/* ===== Usage ===== */

// Wait for .ss__search-header__count-total to appear, then instantiate SearchCountWatcher
function waitForElement(selector, callback) {
  const el = document.querySelector(selector);
  if (el) {
    callback(el);
    return;
  }
  // Observe DOM for the element
  const observer = new MutationObserver(() => {
    const el = document.querySelector(selector);
    if (el) {
      observer.disconnect();
      callback(el);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener('DOMContentLoaded', () => {
  waitForElement('#searchspring-header .ss__search-header--results', () => {
    new SearchCountWatcher(
      '#searchspring-header .ss__search-header--results',
      (value, el) => {

        const totalResultsEl = document.querySelector('.pa-latest-filters__wrapper-left');
        if (totalResultsEl) {
          totalResultsEl.textContent = ` ${value}`;
        }

        // 👉 Your logic here
        // e.g. trigger filters, analytics, UI updates, etc.
      }
    );
  });
});

/* ==========================================
   Filter Drawer Handler
   ========================================== */
class FilterDrawerHandler {
  constructor() {
    this.drawer = document.getElementById('pa-filter-drawer');
    this.overlay = document.getElementById('pa-filter-drawer-overlay');
    this.openButton = document.querySelector('.pa-filters__button');
    this.closeButton = document.getElementById('pa-drawer-close-btn');
    this.backButton = document.getElementById('pa-drawer-back-btn');
    this.headingsList = document.getElementById('pa-drawer-headings-list');
    this.level1 = document.getElementById('pa-drawer-level-1');
    this.level2 = document.getElementById('pa-drawer-level-2');
    this.optionsContainer = document.getElementById('pa-drawer-options-container');
    this.drawerTitle = document.getElementById('pa-drawer-title');
    this.applyButton = document.getElementById('pa-drawer-apply-btn');

    this.currentLevel = 1;
    this.currentFacetElement = null;
    this.priceRanges = [
      { label: 'Under $10', min: 0, max: 10 },
      { label: '$10 - $20', min: 10, max: 20 },
      { label: '$20 - $30', min: 20, max: 30 },
      { label: '$30 - $40', min: 30, max: 40 },
      { label: '$40 - $50', min: 40, max: 50 },
      { label: '$50 - $60', min: 50, max: 60 },
      { label: '$60 - $70', min: 60, max: 70 },
      { label: '$70 - $80', min: 70, max: 80 },
      { label: '$80 - $90', min: 80, max: 90 },
      { label: '$90 - $100', min: 90, max: 100 },
      { label: 'Over $100', min: 100, max: 10000 }
    ];

    // Track pending filter selections
    this.pendingSelections = new Map(); // Map of filter URLs to their state
    this.appliedFilters = new Set(); // Track currently applied filters

    if (!this.drawer || !this.openButton) {
      console.warn('[FilterDrawerHandler] Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    // Open drawer
    this.openButton.addEventListener('click', () => this.openDrawer());

    // Close drawer (without applying)
    this.closeButton.addEventListener('click', () => this.closeDrawer(false));
    this.overlay.addEventListener('click', () => this.closeDrawer(false));

    // Back button
    this.backButton.addEventListener('click', () => this.goBack());

    // Apply button - apply all pending selections
    if (this.applyButton) {
      this.applyButton.addEventListener('click', () => this.applyFilters());
    }

    // Add Clear All button functionality
    this.addClearAllButton();

    // Watch for SearchSpring filters to load
    this.waitForFilters();
  }

  addClearAllButton() {
    // Create Clear All button
    const footerActions = this.drawer.querySelector('.pa-drawer__footer-actions');
    if (!footerActions) return;

    const clearAllBtn = document.createElement('button');
    clearAllBtn.className = 'pa-btn-clear-all';
    clearAllBtn.id = 'pa-drawer-clear-all-btn';
    clearAllBtn.textContent = 'Remove all filters';
    clearAllBtn.type = 'button';

    clearAllBtn.addEventListener('click', () => this.clearAllFilters());

    // Insert before Apply button
    footerActions.insertBefore(clearAllBtn, this.applyButton);
  }

  waitForFilters() {
    // Use MutationObserver to detect when SearchSpring filters are loaded
    const sidebar = document.getElementById('searchspring-sidebar');
    if (!sidebar) return;

    const observer = new MutationObserver(() => {
      const facets = document.querySelectorAll('.ss__facet__header');
      if (facets.length > 0) {
        observer.disconnect();
        // Capture currently applied filters
        this.captureAppliedFilters();
      }
    });

    observer.observe(sidebar, {
      childList: true,
      subtree: true
    });
  }

  captureAppliedFilters() {
    // Capture all currently applied filters
    this.appliedFilters.clear();
    const activeOptions = document.querySelectorAll('.ss__facet-list-options__option--filtered, .ss__facet-options-list__option--active, .ss__facet-palette-options__option--filtered');

    activeOptions.forEach(option => {
      const link = option.querySelector('a[href]');
      if (link) {
        this.appliedFilters.add(link.href);
      }
    });
  }

  openDrawer() {
    // Capture current applied filters
    this.captureAppliedFilters();

    // Reset pending selections to match current state
    this.pendingSelections.clear();
    this.appliedFilters.forEach(url => {
      this.pendingSelections.set(url, true);
    });

    // Populate headings
    this.populateHeadings();

    // Show drawer
    this.drawer.classList.add('active');
    this.overlay.classList.add('active');
    document.body.classList.add('pa-drawer-open');

    // Reset to level 1
    this.showLevel(1);

    // Update button states
    this.updateApplyButtonState();
  }

  closeDrawer(apply = false) {
    if (!apply) {
      // Reset pending selections to applied state
      this.pendingSelections.clear();
      this.appliedFilters.forEach(url => {
        this.pendingSelections.set(url, true);
      });
    }

    this.drawer.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.classList.remove('pa-drawer-open');

    // Reset state
    setTimeout(() => {
      this.showLevel(1);
      this.currentFacetElement = null;
    }, 300);
  }

  applyFilters() {
    // Build the complete filter URL from pending selections
    const selectedUrls = [];
    this.pendingSelections.forEach((selected, url) => {
      if (selected) {
        selectedUrls.push(url);
      }
    });

    // Prepare current URL object and strip pagination from query parameters
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    if (selectedUrls.length === 0) {
      // If no filters selected, clear the hash
      url.hash = '';
      this.closeDrawer(true);
      window.history.pushState(null, '', url.toString());
      this.triggerStateChangeEvents();
      return;
    }

    // SearchSpring uses hash-based routing: /collections/men#/filter:vendor:Nike
    // We combine all individual hash fragments into one
    const allHashFragments = [];
    selectedUrls.forEach(selectedUrl => {
      try {
        const hashIndex = selectedUrl.indexOf('#');
        if (hashIndex !== -1) {
          const hashPart = selectedUrl.substring(hashIndex + 1);
          if (hashPart) {
            // Split fragments like /filter:X/filter:Y
            const parts = hashPart.split(/(?=\/filter:)/);
            parts.forEach(part => {
              if (part.startsWith('/filter:') && !allHashFragments.includes(part)) {
                allHashFragments.push(part);
              }
            });
          }
        }
      } catch (e) {
        console.error('[FilterDrawer] Error parsing URL:', selectedUrl, e);
      }
    });

    // Update URL hash with combined fragments
    url.hash = allHashFragments.join('');

    // Close drawer immediately
    this.closeDrawer(true);

    // Update the URL without a page refresh to satisfy Searchspring-style filtering
    window.history.pushState(null, '', url.toString());

    // Notify Searchspring and other integrations of the URL/hash change
    this.triggerStateChangeEvents();
  }

  /**
   * Helper to trigger manual events for search frameworks that listen to URL changes.
   */
  triggerStateChangeEvents() {
    window.dispatchEvent(new HashChangeEvent('hashchange'));
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  clearAllFilters() {
    // Clear all pending selections
    this.pendingSelections.clear();

    // Also clear applied filters locally
    this.appliedFilters.clear();

    // Directly call applyFilters which will navigate to base URL
    this.applyFilters();
  }

  //   updateResultsCount() {
  //     // Count total pending selections
  //     let count = 0;
  //     this.pendingSelections.forEach(selected => {
  //       if (selected) count++;
  //     });

  //     console.log('[FilterDrawer] Update results count:', count, 'pending selections:', this.pendingSelections.size);

  //     // Update Apply button text
  //     if (this.applyButton) {
  //       if (count > 0) {
  //         this.applyButton.textContent = `Show ${count} result${count !== 1 ? 's' : ''}`;
  //       } else {
  //         this.applyButton.textContent = 'Show results';
  //       }
  //     }
  //   }

  updateApplyButtonState() {
    // Check if there are any changes
    let hasChanges = false;

    // Check for new selections
    this.pendingSelections.forEach((selected, url) => {
      if (selected && !this.appliedFilters.has(url)) {
        hasChanges = true;
      }
    });

    // Check for removed selections
    this.appliedFilters.forEach(url => {
      if (!this.pendingSelections.get(url)) {
        hasChanges = true;
      }
    });

    // Update button state
    if (this.applyButton) {
      this.applyButton.disabled = !hasChanges;
    }

    // this.updateResultsCount();
  }

  populateHeadings() {
    // Clear existing
    this.headingsList.innerHTML = '';

    // Get all filter headers from SearchSpring
    const facetHeaders = document.querySelectorAll('.ss__facet__header');

    if (facetHeaders.length === 0) {
      // Show 'No filters available' message
      this.headingsList.innerHTML = '<li class="pa-drawer-list__item"><p style="padding: 16px 20px; color: #9CA3AF;">No filters available</p></li>';

      // Add 'Remove all filters' button
      const li = document.createElement('li');
      li.className = 'pa-drawer-list__item';
      li.style.display = 'flex';
      li.style.justifyContent = 'center';
      li.style.padding = '8px 0 0 0';

      const clearAllBtn = document.createElement('button');
      clearAllBtn.className = 'pa-btn-clear-all';
      clearAllBtn.id = 'pa-drawer-clear-all-btn-empty';
      clearAllBtn.textContent = 'Remove all filters';
      clearAllBtn.type = 'button';
      clearAllBtn.style.margin = '0 auto';
      clearAllBtn.addEventListener('click', () => this.clearAllFilters());

      li.appendChild(clearAllBtn);
      this.headingsList.appendChild(li);
      return;
    }

    facetHeaders.forEach((header) => {
      const facetContainer = header.closest('.ss__facet');
      if (!facetContainer) return;

      // Get the label text (excluding any icon elements)
      const labelText = Array.from(header.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE)
        .map(node => node.textContent.trim())
        .join(' ') || header.textContent.trim();

      // Get the icon if it exists
      const icon = header.querySelector('.ss__facet__dropdown__icon, .ss__icon, svg');
      let iconHTML = '';
      if (icon) {
        iconHTML = icon.outerHTML;
      }

      // Count pending selections for this facet
      const count = this.countPendingSelectionsForFacet(facetContainer);

      // Create list item
      const li = document.createElement('li');
      li.className = 'pa-drawer-list__item';

      const button = document.createElement('button');
      button.className = 'pa-drawer-list__button';
      button.type = 'button';

      const leftContent = document.createElement('span');
      leftContent.classList.add('pa-drawer-list__button-left');


      if (iconHTML) {
        const iconSpan = document.createElement('span');
        iconSpan.innerHTML = iconHTML;
        iconSpan.style.display = 'flex';
        iconSpan.style.alignItems = 'center';
        leftContent.appendChild(iconSpan);
      }

      const labelSpan = document.createElement('span');
      labelSpan.textContent = labelText;
      leftContent.appendChild(labelSpan);

      button.appendChild(leftContent);

      // Add count and arrow
      const rightContent = document.createElement('span');
      rightContent.style.display = 'flex';
      rightContent.style.alignItems = 'center';
      rightContent.style.gap = '8px';

      if (count > 0) {
        const countSpan = document.createElement('span');
        countSpan.className = 'pa-drawer-list__count';
        countSpan.textContent = count;
        rightContent.appendChild(countSpan);
      }

      const arrow = document.createElement('span');
      arrow.innerHTML = '›';
      arrow.style.fontSize = '20px';
      arrow.style.color = '#9CA3AF';
      rightContent.appendChild(arrow);

      button.appendChild(rightContent);

      // Click handler
      button.addEventListener('click', () => {
        this.openFacetOptions(facetContainer, labelText);
      });

      li.appendChild(button);
      this.headingsList.appendChild(li);
    });
  }

  countPendingSelectionsForFacet(facetContainer) {
    let count = 0;

    // Check traditional list options
    const options = facetContainer.querySelectorAll('.ss__facet-list-options__option a[href], .ss__facet-options-list__option a[href], .ss__facet-palette-options__option a[href]');
    options.forEach(link => {
      if (this.pendingSelections.get(link.href)) {
        count++;
      }
    });

    // Also check for range filters (Price)
    const field = this.getFacetField(facetContainer);
    if (this.getCurrentPriceFilter(field)) {
      count++;
    }

    return count;
  }

  openFacetOptions(facetContainer, title) {
    this.currentFacetElement = facetContainer;

    // Update title
    this.drawerTitle.textContent = title;

    // Show back button
    this.backButton.style.display = 'flex';

    // Handle Price facet specially
    if (title.toLowerCase() === 'price') {
      this.renderPriceFacet(facetContainer);
      this.showLevel(2);
      return;
    }

    // Clone the facet options
    const facetOptions = facetContainer.querySelector('.ss__facet__options, .ss__facet-options');
    if (facetOptions) {
      this.optionsContainer.innerHTML = '';
      const clonedOptions = facetOptions.cloneNode(true);

      // Convert to multi-select checkboxes
      this.convertToMultiSelect(clonedOptions, facetContainer);

      this.optionsContainer.appendChild(clonedOptions);
    }

    // Show level 2
    this.showLevel(2);
  }

  convertToMultiSelect(clonedContainer, originalContainer) {
    // Find all filter option links
    const options = clonedContainer.querySelectorAll('.ss__facet-list-options__option, .ss__facet-options-list__option, .ss__facet-palette-options__option');

    options.forEach(option => {
      const link = option.tagName === 'A' ? option : option.querySelector('a[href]');
      if (!link) return;

      const url = link.href;

      // If the option is an anchor, prevent it from being a link
      if (option.tagName === 'A') {
        option.removeAttribute('href');
        option.addEventListener('click', (e) => e.preventDefault());
      }

      // Extract just the label text
      let labelText = link.textContent.trim();

      // Remove any count in parentheses if it exists
      labelText = labelText.replace(/\s*\(\d+\)\s*$/, '');

      // Check for palette/swatch
      const palette = link.querySelector('.ss__facet-palette-options__option__palette');
      let paletteCopy = null;
      if (palette) {
        paletteCopy = palette.cloneNode(true);
        paletteCopy.style.marginRight = '8px';
        paletteCopy.style.flexShrink = '0';
      }

      // Clear the option content
      option.innerHTML = '';

      // Restructure: Label | Count | Checkbox
      // Create wrapper for the entire option
      const optionWrapper = document.createElement('div');
      optionWrapper.classList.add('pa-filter-option-wrapper');

      // Left side: Label (now with optional palette)
      const labelContent = document.createElement('div');
      labelContent.classList.add('pa-filter-option-label');

      if (paletteCopy) {
        labelContent.appendChild(paletteCopy);
      }

      const labelSpan = document.createElement('span');
      labelSpan.textContent = labelText;
      labelSpan.classList.add('pa-filter-option-label-text');
      labelContent.appendChild(labelSpan);


      // Right side: Checkbox
      const checkbox = document.createElement('input');
      checkbox.classList.add('pa-filter-checkbox');
      checkbox.type = 'checkbox';
      checkbox.checked = this.pendingSelections.get(url) || false;


      // Add checkbox change handler
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        console.log('[FilterDrawer] Checkbox changed:', {
          url: url,
          checked: checkbox.checked,
          pendingSelections: this.pendingSelections
        });
        this.pendingSelections.set(url, checkbox.checked);
        this.updateApplyButtonState();

        // Update the visual state
        if (checkbox.checked) {
          option.classList.add('ss__facet-list-options__option--filtered');
          option.classList.add('ss__facet-palette-options__option--filtered');
          labelSpan.style.fontWeight = '600';
        } else {
          option.classList.remove('ss__facet-list-options__option--filtered');
          option.classList.remove('ss__facet-palette-options__option--filtered');
          labelSpan.style.fontWeight = 'normal';
        }

        // Update count in level 1
        this.updateFacetCount(originalContainer);
      });

      // Make the entire wrapper clickable
      optionWrapper.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.target !== checkbox) {
          e.preventDefault();
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change'));
        }
      });

      // Assemble the structure
      optionWrapper.appendChild(labelContent);

      optionWrapper.appendChild(checkbox);
      option.appendChild(optionWrapper);

      // Update visual state based on pending selection
      if (this.pendingSelections.get(url)) {
        option.classList.add('ss__facet-list-options__option--filtered');
        option.classList.add('ss__facet-palette-options__option--filtered');
        labelSpan.style.fontWeight = '600';
      }

      // Add hover effect
      optionWrapper.addEventListener('mouseenter', () => {
        optionWrapper.style.backgroundColor = '#F9FAFB';
      });
      optionWrapper.addEventListener('mouseleave', () => {
        optionWrapper.style.backgroundColor = 'transparent';
      });
    });
  }

  updateFacetCount(facetContainer) {
    // Update the count badge in level 1 for this facet
    const count = this.countPendingSelectionsForFacet(facetContainer);

    // Find the corresponding list item in level 1
    const facetLabel = facetContainer.querySelector('.ss__facet__header')?.textContent.trim();
    if (!facetLabel) return;

    const listItems = this.headingsList.querySelectorAll('.pa-drawer-list__item');
    listItems.forEach(item => {
      const button = item.querySelector('.pa-drawer-list__button');
      const label = button?.textContent.trim();

      if (label && label.includes(facetLabel)) {
        const countSpan = button.querySelector('.pa-drawer-list__count');

        if (count > 0) {
          if (countSpan) {
            countSpan.textContent = count;
          } else {
            // Create count span
            const newCountSpan = document.createElement('span');
            newCountSpan.className = 'pa-drawer-list__count';
            newCountSpan.textContent = count;

            const rightContent = button.querySelector('span:last-child');
            if (rightContent) {
              rightContent.insertBefore(newCountSpan, rightContent.firstChild);
            }
          }
        } else if (countSpan) {
          countSpan.remove();
        }
      }
    });
  }

  goBack() {
    this.drawerTitle.textContent = 'Filter';
    this.backButton.style.display = 'none';
    this.showLevel(1);
    this.currentFacetElement = null;
  }

  getFacetField(facetContainer) {
    let field = '';
    if (facetContainer.dataset.field) {
      field = facetContainer.dataset.field.toLowerCase();
    } else {
      const classes = Array.from(facetContainer.classList);
      const fieldClass = classes.find(c => c.startsWith('ss__facet--'));
      if (fieldClass) {
        field = fieldClass.replace('ss__facet--', '').toLowerCase();
      }
    }

    if (!field || field === 'price') return 'ss_price';
    return field;
  }

  getCurrentPriceFilter(field) {
    const filterPrefix = `filter:${field}:`;
    let current = null;
    this.pendingSelections.forEach((selected, url) => {
      if (selected && url.includes(filterPrefix)) {
        // Handle format like filter:price:10:20
        const regex = new RegExp(`filter:${field}:(\\d+):(\\d+)`);
        const match = url.match(regex);
        if (match) {
          current = { min: parseInt(match[1]), max: parseInt(match[2]), url: url };
        }
      }
    });
    return current;
  }

  renderPriceFacet(facetContainer) {
    this.optionsContainer.innerHTML = '';
    const field = this.getFacetField(facetContainer);
    const currentPrice = this.getCurrentPriceFilter(field);

    const list = document.createElement('ul');
    list.className = 'pa-drawer-list';

    this.priceRanges.forEach(range => {
      const li = document.createElement('li');
      li.className = 'pa-drawer-list__item';

      const optionWrapper = document.createElement('div');
      optionWrapper.className = 'pa-price-range-option';

      const labelSpan = document.createElement('span');
      labelSpan.textContent = range.label;


      const radio = document.createElement('div');
      radio.className = 'pa-price-radio' + (currentPrice && currentPrice.min === range.min && currentPrice.max === range.max ? ' selected' : '');

      // Inline styles for high priority, but classes help too
      radio.style.width = '20px';
      radio.style.height = '20px';
      radio.style.borderRadius = '50%';
      radio.style.border = '1px solid #9CA3AF';
      radio.style.position = 'relative';
      radio.style.flexShrink = '0';

      if (radio.classList.contains('selected')) {
        radio.style.borderColor = '#2F3D4D';
        const innerCircle = document.createElement('div');
        innerCircle.style.position = 'absolute';
        innerCircle.style.top = '50%';
        innerCircle.style.left = '50%';
        innerCircle.style.transform = 'translate(-50%, -50%)';
        innerCircle.style.width = '10px';
        innerCircle.style.height = '10px';
        innerCircle.style.borderRadius = '50%';
        innerCircle.style.backgroundColor = '#2F3D4D';
        radio.appendChild(innerCircle);
        labelSpan.style.fontWeight = '600';
      }

      optionWrapper.appendChild(labelSpan);
      optionWrapper.appendChild(radio);

      optionWrapper.addEventListener('click', () => {
        this.selectPriceRange(range, facetContainer);
      });

      // Hover effect
      optionWrapper.addEventListener('mouseenter', () => {
        optionWrapper.style.backgroundColor = '#F9FAFB';
      });
      optionWrapper.addEventListener('mouseleave', () => {
        optionWrapper.style.backgroundColor = 'transparent';
      });

      li.appendChild(optionWrapper);
      list.appendChild(li);
    });

    this.optionsContainer.appendChild(list);
  }

  selectPriceRange(range, facetContainer) {
    const field = this.getFacetField(facetContainer);
    const filterPrefix = `filter:${field}:`;
    const isSelected = this.getCurrentPriceFilter(field)?.min === range.min && this.getCurrentPriceFilter(field)?.max === range.max;

    // 1. Clear existing price filters for this field
    this.pendingSelections.forEach((selected, url) => {
      if (url.includes(filterPrefix)) {
        this.pendingSelections.set(url, false);
      }
    });

    // 2. Add new if not already selected (toggle behavior)
    if (!isSelected) {
      const baseUrl = window.location.href.split('#')[0];
      const newUrl = `${baseUrl}#/filter:${field}:${range.min}:${range.max}`;
      this.pendingSelections.set(newUrl, true);

      // 3. Update original slider if present
      const minInput = facetContainer.querySelector('.ss__facet-slider__input--min');
      const maxInput = facetContainer.querySelector('.ss__facet-slider__input--max');
      if (minInput && maxInput) {
        minInput.value = range.min;
        maxInput.value = range.max;
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
        maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 4. UI Updates
    this.renderPriceFacet(facetContainer);
    this.updateApplyButtonState();
    this.updateFacetCount(facetContainer);
  }

  showLevel(level) {
    this.currentLevel = level;

    if (level === 1) {
      this.level1.classList.add('active');
      this.level2.classList.remove('active');
      this.level1.style.transform = 'translateX(0)';
      this.level1.style.opacity = '1';
      this.level2.style.transform = 'translateX(20%)';
      this.level2.style.opacity = '0';
    } else {
      this.level1.classList.remove('active');
      this.level2.classList.add('active');
      this.level1.style.transform = 'translateX(-20%)';
      this.level1.style.opacity = '0';
      this.level2.style.transform = 'translateX(0)';
      this.level2.style.opacity = '1';
    }
  }
}

// Initialize Filter Drawer and URL Cleanup
document.addEventListener('DOMContentLoaded', () => {
  new FilterDrawerHandler();

  // Global handler to strip ?page=X when hash filters are active
  // This handles direct clicks on filters outside the custom drawer.
  const stripPaginationFromUrl = () => {
    const url = new URL(window.location.href);
    const hasFilters = url.hash.includes('/filter:');
    const hasPageParam = url.searchParams.has('page');

    if (hasFilters && hasPageParam) {
      url.searchParams.delete('page');
      // Use replaceState to clean the URL without creating extra history entries
      window.history.replaceState(null, '', url.toString());
    }
  };

  // Run initial check and subscribe to hash changes
  stripPaginationFromUrl();
  window.addEventListener('hashchange', stripPaginationFromUrl);



  // ─────────────────────────────────────────────────────────────────────────────
  // VariantLimiter — limits visible variant options per product card
  // and appends a "+N" ellipsis anchor for the hidden remainder.
  // ─────────────────────────────────────────────────────────────────────────────
  class VariantLimiter {
    constructor({
      containerSelector = '#searchspring-content .latest-variant-option',
      optionSelector = '.latest-option:not([data-inventory="0"])',
      limit = 100,
      ellipsisClass = 'pa-variants-ellipsis'
    } = {}) {
      this.containerSelector = containerSelector;
      this.optionSelector = optionSelector;
      this.limit = limit;
      this.ellipsisClass = ellipsisClass;
    }

    /**
     * Run the limiter across all product cards currently in the DOM.
     * Safe to call multiple times — idempotent per container thanks to
     * the stale-ellipsis removal inside limitVariants().
     */
    run() {
      const containers = document.querySelectorAll(this.containerSelector);
      containers.forEach(container => this.limitVariants(container));
    }

    limitVariants(container) {
      const options = container.querySelectorAll(this.optionSelector);
      const total = options.length;

      if (total <= this.limit) return;

      // Show only up to the limit, hide the rest
      options.forEach((option, idx) => {
        option.style.display = idx < this.limit ? '' : 'none';
      });

      const remaining = total - this.limit;

      // Remove any pre-existing ellipsis (prevents duplicates on re-render)
      const oldEllipsis = container.querySelector(`.${this.ellipsisClass}`);
      if (oldEllipsis) oldEllipsis.remove();

      // Resolve a product link from the first hidden option
      let productLink = '#';
      for (let i = this.limit; i < total; i++) {
        const option = options[i];
        let anchor = option.querySelector('a[href]');
        if (!anchor && option.tagName === 'A' && option.hasAttribute('href')) {
          anchor = option;
        }
        if (anchor) {
          productLink = anchor.getAttribute('href');
          break;
        }
      }

      // Append the "+N" ellipsis anchor
      const moreAnchor = document.createElement('a');
      moreAnchor.className = this.ellipsisClass;
      moreAnchor.innerText = `+${remaining}`;
      moreAnchor.href = productLink || '#';
      moreAnchor.style.cursor = 'pointer';
      container.appendChild(moreAnchor);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // VariantLimiter initialisation — hooks into Searchspring's afterStore event
  // ─────────────────────────────────────────────────────────────────────────────
  (function initVariantLimiter() {
    const limiter = new VariantLimiter();

    /**
     * Register an afterStore listener on every Searchspring controller that
     * is present in window.searchspring.controller at call-time.
     * afterStore fires after Searchspring has fetched results AND updated the
     * DOM — the correct moment to (re-)apply the variant limiter.
     *
     * We use a tiny flag per controller so we never double-bind.
     */
    function bindToControllers() {
      const ssControllers = window.searchspring && window.searchspring.controller;
      if (!ssControllers) return false;

      const ids = Object.keys(ssControllers);
      if (ids.length === 0) return false;

      ids.forEach(id => {
        const ctrl = ssControllers[id];
        if (!ctrl || ctrl.__variantLimiterBound) return;

        ctrl.on('afterStore', async (event, next) => {
          // Let Searchspring finish its own afterStore work first
          await next();

          // Tiny rAF to ensure the DOM paint has completed before we process
          requestAnimationFrame(() => limiter.run());
        });

        ctrl.__variantLimiterBound = true;
      });

      return true;
    }

    /**
     * Searchspring controllers are created asynchronously after the bundle
     * script executes. We poll briefly (max ~3 s) with exponential back-off
     * until the controllers appear, then bind once and clean up.
     * This is NOT a performance-heavy loop — it self-terminates.
     */
    function waitForControllers(attempt, delay) {
      if (bindToControllers()) {
        // ✅ Bound successfully — also run immediately for the initial paint
        limiter.run();
        return;
      }

      if (attempt >= 15) {
        // Give up after ~3 s; fall back to a one-shot run for initial load
        limiter.run();
        return;
      }

      setTimeout(() => waitForControllers(attempt + 1, Math.min(delay * 1.5, 500)), delay);
    }

    // Kick off — start polling 100 ms after DOMContentLoaded so the bundle
    // has had a chance to begin its async setup.
    waitForControllers(0, 100);
  })();


});

/* ==========================================
   Sorting Clone Handler
   Clones .ss__sorting into .pa-filters-sort__wrapper
   and keeps them synchronized
   ========================================== */
(function () {
  const CLONE_MARKER = 'pa-sorting-clone';
  let isInitialized = false;

  function initSortingClone() {
    const original = document.querySelector('.ss__sorting');
    const wrapper = document.querySelector('.pa-filters-sort__wrapper');

    if (!original || !wrapper) return;

    // Prevent duplicate cloning
    if (wrapper.querySelector('.' + CLONE_MARKER)) return;

    // Hide the original
    original.style.display = 'none';

    const originalSelect = original.querySelector('select');
    if (!originalSelect) return;

    // Create custom styled wrapper
    const customWrapper = document.createElement('div');
    customWrapper.className = CLONE_MARKER + ' pa-sort-dropdown';

    // Create the visible label that shows "Sort by: [selected option]"
    const label = document.createElement('span');
    label.className = 'pa-sort-label';

    // Create chevron icon
    const chevron = document.createElement('span');
    chevron.className = 'pa-sort-chevron';
    chevron.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

    // Clone the select and make it invisible but functional
    const cloneSelect = originalSelect.cloneNode(true);
    cloneSelect.className = 'pa-sort-select-hidden';
 
    // Update label with current selection
    function updateLabel() {
      const selectedOption = cloneSelect.options[cloneSelect.selectedIndex];
      if (selectedOption) {
        label.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M3 6.75C3 6.33579 3.33579 6 3.75 6H20.25C20.6642 6 21 6.33579 21 6.75C21 7.16421 20.6642 7.5 20.25 7.5H3.75C3.33579 7.5 3 7.16421 3 6.75ZM3 12C3 11.5858 3.33579 11.25 3.75 11.25H20.25C20.6642 11.25 21 11.5858 21 12C21 12.4142 20.6642 12.75 20.25 12.75H3.75C3.33579 12.75 3 12.4142 3 12ZM3 17.25C3 16.8358 3.33579 16.5 3.75 16.5H12C12.4142 16.5 12.75 16.8358 12.75 17.25C12.75 17.6642 12.4142 18 12 18H3.75C3.33579 18 3 17.6642 3 17.25Z" fill="#2F3D4D"/>
</svg><strong>Sort by:</strong> ${selectedOption.text}`;
      }
    }

    // Initial label update
    updateLabel();
    wrapper.style.opacity = '1'

    // Sync clone -> original and update label
    cloneSelect.addEventListener('change', function () {
      originalSelect.value = cloneSelect.value;
      updateLabel();

      // Trigger native events on original so SearchSpring picks it up
      originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      originalSelect.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Sync original -> clone (when SearchSpring resets sorting on clear filters)
    function syncFromOriginal() {
      // SearchSpring may re-render, so re-query the original select
      const freshOriginalSelect = document.querySelector('.ss__sorting:not(.' + CLONE_MARKER + ') select');
      if (freshOriginalSelect && cloneSelect.value !== freshOriginalSelect.value) {
        cloneSelect.value = freshOriginalSelect.value;
        updateLabel();
      }
    }

    // Watch for changes to original select (SearchSpring may update it)
    const originalObserver = new MutationObserver(syncFromOriginal);
    originalObserver.observe(originalSelect, {
      attributes: true,
      childList: true,
      subtree: true
    });

    // Also listen for hash changes (filter clear triggers this)
    // Use setTimeout to allow SearchSpring to finish re-rendering
    window.addEventListener('hashchange', () => {
      setTimeout(syncFromOriginal, 300);
      setTimeout(syncFromOriginal, 600);
      setTimeout(syncFromOriginal, 1000);
    });
    window.addEventListener('popstate', () => {
      setTimeout(syncFromOriginal, 300);
      setTimeout(syncFromOriginal, 600);
      setTimeout(syncFromOriginal, 1000);
    });

    // Assemble the custom dropdown
    customWrapper.appendChild(label);
    customWrapper.appendChild(chevron);
    customWrapper.appendChild(cloneSelect);

    // Clear wrapper and append custom dropdown
    wrapper.innerHTML = '';
    wrapper.appendChild(customWrapper);

    // Add inline styles for the custom dropdown
    addSortDropdownStyles();

    isInitialized = true;
  }

  function addSortDropdownStyles() {
    if (document.getElementById('pa-sort-dropdown-styles')) return;

    const style = document.createElement('style');
    style.id = 'pa-sort-dropdown-styles';
    style.textContent = `
      .pa-sort-dropdown {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        font-size: 14px;
        color: #2F3D4D;
        padding: 8px 0;
      }
      .pa-sort-label {
        pointer-events: none;
      }
      .pa-sort-label strong {
        font-weight: 600;
      }
      .pa-sort-chevron {
        display: flex;
        align-items: center;
        pointer-events: none;
      }
      .pa-sort-chevron svg {
        width: 16px;
        height: 16px;
      }
      .pa-sort-select-hidden {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        font-size: 16px;
      }
    `;
    document.head.appendChild(style);
  }

  // Watch for SearchSpring to load/reload the sorting element
  function watchForSorting() {
    // Initial attempt
    initSortingClone();

    // Observe for dynamic loads (AJAX reloads)
    const observer = new MutationObserver(() => {
      const original = document.querySelector('.ss__sorting:not(.' + CLONE_MARKER + ')');
      const wrapper = document.querySelector('.pa-filters-sort__wrapper');

      if (original && wrapper) {
        // Check if original is visible (SearchSpring may have re-rendered)
        if (original.style.display !== 'none') {
          // Remove old clone if exists
          const oldClone = wrapper.querySelector('.' + CLONE_MARKER);
          if (oldClone) oldClone.remove();

          // Re-initialize
          initSortingClone();
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup after 30 seconds to prevent memory leaks
    setTimeout(() => {
      if (isInitialized) {
        observer.disconnect();
      }
    }, 30000);
  }

  // Start watching on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForSorting);
  } else {
    watchForSorting();
  }
})();